import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Lock, Unlock, Calculator, AlertTriangle } from 'lucide-react';
import { StakingPool } from '../../types/wallet';

interface StakeModalProps {
  pool: StakingPool;
  onClose: () => void;
}

export function StakeModal({ pool, onClose }: StakeModalProps) {
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Mock user balance
  const userBalance = 15000;
  const amountNum = parseFloat(amount) || 0;
  
  // Calculate estimated rewards
  const dailyReward = (amountNum * pool.apy) / 365 / 100;
  const monthlyReward = dailyReward * 30;
  const yearlyReward = amountNum * (pool.apy / 100);

  const handleMaxClick = () => {
    setAmount(userBalance.toString());
  };

  const handleStake = async () => {
    if (!amount || amountNum <= 0) return;
    
    setIsLoading(true);
    try {
      // In real app, would call smart contract
      console.log(`Staking ${amount} ${pool.token} in pool ${pool.id}`);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate transaction
      onClose();
    } catch (error) {
      console.error('Staking failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Stake {pool.token}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Pool Info */}
          <Card className="bg-slate-50 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">{pool.name}</h3>
                <div className="flex gap-2">
                  <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    {pool.apy}% APY
                  </Badge>
                  {pool.lockPeriod > 0 ? (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      {pool.lockPeriod}d
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Unlock className="h-3 w-3" />
                      Flexible
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Min. Stake</div>
                  <div className="font-medium">100 {pool.token}</div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Lock Period</div>
                  <div className="font-medium">
                    {pool.lockPeriod > 0 ? `${pool.lockPeriod} days` : 'None'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label>Amount to Stake</Label>
            <div className="relative">
              <Input
                type="number"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pr-16"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMaxClick}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 px-2 text-xs"
              >
                MAX
              </Button>
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Balance: {userBalance.toLocaleString()} {pool.token}
            </div>
          </div>

          {/* Estimated Rewards */}
          {amountNum > 0 && (
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calculator className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-700 dark:text-blue-300">Estimated Rewards</span>
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="text-center">
                    <div className="text-slate-500 dark:text-slate-400">Daily</div>
                    <div className="font-semibold">{dailyReward.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 dark:text-slate-400">Monthly</div>
                    <div className="font-semibold">{monthlyReward.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-500 dark:text-slate-400">Yearly</div>
                    <div className="font-semibold">{yearlyReward.toFixed(2)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warning for locked staking */}
          {pool.lockPeriod > 0 && amountNum > 0 && (
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                <div className="text-sm text-orange-700 dark:text-orange-300">
                  <div className="font-medium">Locked Staking Warning</div>
                  <div className="mt-1">
                    Your tokens will be locked for {pool.lockPeriod} days. You cannot unstake before the lock period expires.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleStake}
              disabled={!amount || amountNum <= 0 || amountNum > userBalance || isLoading}
              className="flex-1"
            >
              {isLoading ? 'Staking...' : `Stake ${amount || '0'} ${pool.token}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}